import { Scope } from '@talpor/react-context-manager';

import api, { request } from '../../api';
import { IStore } from '../index';

export interface ITeamsActions extends Scope<IStore> {
  getTeams: (state: IStore) => () => Promise<IStore>;
  getTeam: (state: IStore) => (teamId: string, member?: string) => Promise<IStore>;
  setCurrentTeam: (
    state: IStore
  ) => (currentTeam: any, currentMemberId?: string) => Promise<IStore>;
  updateTeam: (state: IStore) => (key: string, value: number | string) => Promise<IStore>;
  deactivateTeam: (state: IStore) => (id: string) => Promise<IStore>;
  channelsJoin: (state: IStore) => (data: any) => Promise<IStore>;
  channelSet: (state: IStore) => (data: any) => Promise<IStore>;
  sendInvitation: (state: IStore) => (email: any) => Promise<IStore>;
  getInvitations: (state: IStore) => () => Promise<IStore>;
  revokeInvitation: (state: IStore) => (id: string) => Promise<IStore>;
  removeManager: (state: IStore) => (id: string) => Promise<IStore>;
  getSummaries: (state: IStore) => (team: string, platform: string) => Promise<IStore>;
  sendPayment: (state: IStore) => (team: string, month: string, orderId: string) => Promise<IStore>;
}

const persistData = (team: any, key?: string, isString?: boolean): void => {
  localStorage.setItem(!key ? 'team' : key, !isString ? JSON.stringify(team) : team);
};

export const teamsActions: ITeamsActions = {
  getTeams: (state: IStore) => async () => {
    const teams = await request.get(api.teams);
    if (teams.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: teams.error.body.message.message,
            type: 'error',
          },
        },
      };
    }

    return {
      teams: {
        ...state.teams,
        teams,
      },
      misc: {
        loading: false,
        notification: {
          status: false,
          message: '',
          type: 'success',
        },
      },
    } as IStore;
  },
  getTeam: (state: IStore) => async (teamId: string, member?: string) => {
    const currentTeam = await request.get(api.teamDetails(teamId));

    if (currentTeam.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: currentTeam.error.body.message.message,
            type: 'error',
          },
        },
      };
    }

    persistData(currentTeam);
    if (member) {
      await persistData(member, 'member');
    }

    return {
      teams: {
        ...state.teams,
        currentTeam,
        currentMemberId: member,
      },
    } as IStore;
  },
  setCurrentTeam: (state: IStore) => async (currentTeam: any, currentMemberId?: string) => {
    await persistData(currentTeam);
    if (currentMemberId) {
      await persistData(currentMemberId, 'member');
    }

    return {
      ...state,
      teams: {
        ...state.teams,
        currentTeam,
        currentMemberId,
      },
      data: {
        channelData: [],
        data: [],
        score: 0.5,
        timeData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        timeDataArray: [[], [], [], [], [], [], [], [], [], []],
        memberData: [],
        moodData: { positive: 0, neutral: 0, negative: 0, total: 0 },
      },
    };
  },
  updateTeam: (state: IStore) => async (key: string, value: number | string) => {
    const data = { [key]: value };

    const currentTeam = await request.put(
      api.teamDetails(state.teams.currentTeam.teamId),
      data,
      true
    );

    if (currentTeam.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: 'Something went wrong with the update. Please try again',
            type: 'error',
          },
        },
      };
    }
    persistData(currentTeam);

    return {
      ...state,
      teams: {
        ...state.teams,
        currentTeam,
      },
      misc: {
        ...state.misc,
        loading: false,
        notification: { status: true, message: 'Your settings have been saved!', type: 'success' },
      },
    };
  },
  deactivateTeam: (state: IStore) => async (id: string) => {
    const response = await request.delete(api.teamDetails(state.teams.currentTeam.teamId));

    if (response.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: 'Something went wrong with the remove. Please contact us at support@sensla.io',
            type: 'error',
          },
        },
      };
    }

    persistData({});
    persistData({}, 'member');
    const newTeams = [...state.teams.teams];
    const idx = newTeams.map(t => t.teamId).indexOf(id);
    const teams = newTeams.slice(idx, 1);

    return {
      ...state,
      teams: {
        ...state.teams,
        currentTeam: undefined,
        teams,
      },
      misc: {
        ...state.misc,
        loading: false,
        notification: { status: true, message: 'Your team has been removed!', type: 'success' },
      },
    };
  },
  channelsJoin: (state: IStore) => async (data: any) => {
    const currentTeam = await request.post(api.join, data, true);

    if (currentTeam.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: 'Something went wrong trying set your settings. Please try again later!',
            type: 'error',
          },
        },
      };
    }

    persistData(currentTeam);

    return {
      ...state,
      teams: {
        ...state.teams,
        currentTeam,
      },
      misc: {
        ...state.misc,
        loading: false,
        notification: {
          status: true,
          message: 'Your channels have been configured!',
          type: 'success',
        },
      },
    } as IStore;
  },
  channelSet: (state: IStore) => async (data: any) => {
    let currentTeam;
    const dataToBeSent = { team: state.teams.currentTeam.teamId, channels: data.channels };
    if (data.set) {
      currentTeam = await request.post(api.join, dataToBeSent, true);
    } else {
      currentTeam = await request.post(api.leave, dataToBeSent, true);
    }

    if (currentTeam.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: 'Something went wrong trying set your settings. Please try again later!',
            type: 'error',
          },
        },
      };
    }

    persistData(currentTeam);

    return {
      ...state,
      teams: {
        ...state.teams,
        currentTeam,
      },
      misc: {
        ...state.misc,
        loading: false,
        notification: {
          status: true,
          message: 'Channel configured successfully!',
          type: 'success',
        },
      },
    } as IStore;
  },
  sendInvitation: (state: IStore) => async (email: any) => {
    const data = { team: state.teams.currentTeam.teamId, userEmail: email };
    const response = await request.post(api.invitations, data, true);

    if (response.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: response.error.body.message,
            type: 'error',
          },
        },
      };
    }
    // if (response.invitation) {
    const newInvitations = [...state.teams.invitations, response.invitation];

    return {
      teams: {
        ...state.teams,
        invitations: newInvitations,
      },
      misc: {
        ...state.misc,
        loading: false,
        notification: {
          status: true,
          message: 'Invitation has been sent successfully!',
          type: 'success',
        },
      },
    } as IStore;
    // } else {
    //   const newTeams = response.teams;
    //   const newCurrentTeam = newTeams.filter(
    //     (team: any) => team.teamId === state.teams.currentTeam.teamId
    //   )[0];

    //   persistData(newCurrentTeam);

    //   return {
    //     teams: {
    //       ...state.teams,
    //       teams: newTeams,
    //       currentTeam: newCurrentTeam,
    //     },
    //     misc: {
    //       ...state.misc,
    //       loading: false,
    //       notification: {
    //         status: true,
    //         message: 'User has been added to this team successfully!',
    //         type: 'success',
    //       },
    //     },
    //   } as IStore;
    // }
  },
  getInvitations: (state: IStore) => async () => {
    const invitations = await request.get(
      `${api.invitations}?team=${state.teams.currentTeam.teamId}`
    );

    if (invitations.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: 'Something went wrong. Please try again!',
            type: 'error',
          },
        },
      };
    }

    return {
      teams: {
        ...state.teams,
        invitations,
      },
    } as IStore;
  },
  revokeInvitation: (state: IStore) => async (id: any) => {
    const invitation = await request.delete(api.invitationDetails(id));

    if (invitation.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: invitation.error.body.message,
            type: 'error',
          },
        },
      };
    }

    const newInvitations = state.teams.invitations.filter((inv: any) => inv.id !== id);

    return {
      ...state,
      teams: {
        ...state.teams,
        invitations: newInvitations,
      },
      misc: {
        ...state.misc,
        loading: false,
        notification: {
          status: true,
          message: 'Invitation has been revoked!',
          type: 'success',
        },
      },
    } as IStore;
  },
  removeManager: (state: IStore) => async (id: any) => {
    const teams = await request.delete(api.teamManagersDetails(state.teams.currentTeam.teamId, id));

    if (teams.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: teams.error.body.message,
            type: 'error',
          },
        },
      };
    }

    const newTeams = teams;
    const newCurrentTeam = newTeams.filter(
      (team: any) => team.teamId === state.teams.currentTeam.teamId
    )[0];

    persistData(newCurrentTeam);

    return {
      ...state,
      teams: {
        ...state.teams,
        teams: newTeams,
        currentTeam: newCurrentTeam,
      },
      misc: {
        ...state.misc,
        loading: false,
        notification: {
          status: true,
          message: 'Manager has been removed successfully!',
          type: 'success',
        },
      },
    } as IStore;
  },
  getSummaries: (state: IStore) => async (team: string, platform: string) => {
    const summaries = await request.get(api.summaries(team, platform));
    if (summaries.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: summaries.error.body.message.message,
            type: 'error',
          },
        },
      };
    }

    return {
      teams: {
        ...state.teams,
        summaries,
      },
      misc: {
        loading: false,
        notification: {
          status: false,
          message: '',
          type: 'success',
        },
      },
    } as IStore;
  },
  sendPayment: (state: IStore) => async (team: string, month: string, orderId: string) => {
    const data = { team, month, orderId };

    const summaries = await request.post(api.summary, data, true);
    if (summaries.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: 'We can not validate your payment, please verify later or contact support!',
            type: 'error',
          },
        },
      };
    }

    return {
      teams: {
        ...state.teams,
        summaries,
      },
      misc: {
        loading: false,
        notification: {
          status: false,
          message: '',
          type: 'success',
        },
      },
    } as IStore;
  },
};
